import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useLazyQuery } from '@apollo/client';
import { InputGroup, InputRightElement } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import SunshineIcon from 'components/_core/SunshineIcon';

import { OnboardingPackagesDocument } from '../../../graphql/queries/onboardingPackages.gql';
import { OnboardingPackageItem } from '../../../machine/model';
import locales from './locales';

const couponSchema = z
  .object({
    coupon: z.string(),
  })
  .required();

interface PackageCouponInputProps {
  onCouponApplied: (couponId: string) => void;
  appliedCoupon: string | null;
  pack: OnboardingPackageItem;
  companyProfileId: string;
}

const PackageCouponInput: FC<PackageCouponInputProps> = ({
  appliedCoupon,
  companyProfileId,
  onCouponApplied,
  pack,
}) => {
  const { formState, handleSubmit, register, setError, watch } = useForm<
    z.infer<typeof couponSchema>
  >({
    resolver: zodResolver(couponSchema),
  });

  const couponInput = watch('coupon');
  const [checkValidity] = useLazyQuery(OnboardingPackagesDocument, {
    fetchPolicy: 'network-only',
  });

  return (
    <form
      onSubmit={handleSubmit(async ({ coupon }) => {
        if (!coupon) {
          return;
        }

        const { data } = await checkValidity({
          variables: {
            companyProfileId,
            input: {
              coupon,
              type: pack.type,
            },
          },
        });

        /**
         * Retrieve the coupon from the graphql response, ensuring it was valid in the first place
         */
        const validCoupon = data?.viewer.company.onboardingPackages.find(
          ({ providerPackageId }) =>
            providerPackageId === pack.providerPackageId,
        )?.coupon;

        if (!validCoupon) {
          setError('coupon', {
            message: locales.invalidCoupon,
          });

          return;
        }

        onCouponApplied(validCoupon);
      })}
    >
      <Form.Field
        error={formState.errors.coupon?.message}
        isDisabled={Boolean(appliedCoupon)}
        label={locales.label}
      >
        <InputGroup>
          <Form.Input
            {...register('coupon', {
              /**
               * Remove the error message when the user updates the input
               */
              onChange: () =>
                formState.errors.coupon
                  ? setError('coupon', { message: '' })
                  : null,
              /**
               * Input is readonly if a coupon is already applied
               */
              value: appliedCoupon ?? undefined,
            })}
            placeholder={locales.placeholder}
          />

          <InputRightElement paddingRight="space-16" width="auto">
            {appliedCoupon ? (
              <SunshineIcon color="grey.700" name="tick" />
            ) : (
              <Button
                isLoading={formState.isSubmitting}
                size="sm"
                type="submit"
                variant="inline-secondary"
                visibility={couponInput ? 'visible' : 'hidden'}
              >
                {locales.save}
              </Button>
            )}
          </InputRightElement>
        </InputGroup>
      </Form.Field>
    </form>
  );
};

export default PackageCouponInput;
